import React, { useEffect, useState } from "react";
import { Alert, Text, View } from "react-native";
import { KeyRound, Power, Save, Search, ShieldAlert, ShieldCheck, Trash2, UserCheck } from "lucide-react-native";
import { api } from "../api/client";
import { ActionMenu, Badge, Button, Card, DataTable, Field, IconButton, PasswordInput, ScreenHeader, Select, Toolbar } from "../components/UI";
import { styles } from "../styles";
import { getRoleLabel, normalizeRole } from "../utils/format";

const empty = { username: "", email: "", name: "", phone: "", role: "sales", is_active: 1, password: "" };

export function UsersScreen({ user: currentUser }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState(null);

  const isCurrentSuperAdmin = normalizeRole(currentUser?.role) === "superadmin" || Number(currentUser?.id) === 1;

  const load = () => api.users(q, role, status).then(setRows).catch((error) => Alert.alert("Users", error.message));

  useEffect(() => { load(); }, []);

  const isTargetSuperAdmin = (target) => {
    if (!target) return false;
    return Number(target.id) === 1 || normalizeRole(target.role) === "superadmin";
  };

  const isEditingSuperAdmin = isTargetSuperAdmin(form);

  const startEdit = (userRow) => {
    if (isTargetSuperAdmin(userRow) && !isCurrentSuperAdmin) {
      return Alert.alert("Permission Denied", "Administrators cannot modify the Super Admin account.");
    }
    setForm({ ...userRow, password: "" });
  };

  const save = async () => {
    try {
      if (isEditingSuperAdmin && !isCurrentSuperAdmin) {
        return setMessage({ type: "error", text: "Administrators cannot modify the Super Admin account." });
      }
      if (!form.id && !form.password) return setMessage({ type: "error", text: "Password is required for new users." });
      const saved = await api.saveUser(form);
      setForm(empty);
      setMessage({ type: "success", text: `${saved.name || saved.email || saved.username} saved successfully.` });
      load();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  };

  const resetPassword = async (id) => {
    const target = rows.find((r) => Number(r.id) === Number(id));
    if (isTargetSuperAdmin(target) && !isCurrentSuperAdmin) {
      return Alert.alert("Permission Denied", "Administrators cannot reset the Super Admin's password.");
    }
    const password = prompt("Enter new password");
    if (!password) return;
    try {
      await api.resetUserPassword(id, password);
      setMessage({ type: "success", text: "Password reset successfully." });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  };

  const toggle = async (id) => {
    const target = rows.find((r) => Number(r.id) === Number(id));
    if (isTargetSuperAdmin(target)) {
      return Alert.alert("Protected Account", "The Super Admin account cannot be deactivated.");
    }
    try {
      await api.toggleUserActive(id);
      load();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  };

  const removeUser = async (userRow) => {
    if (isTargetSuperAdmin(userRow)) {
      return Alert.alert("Protected Account", "The Super Admin account is permanent and cannot be deleted by anyone.");
    }
    if (currentUser?.id && Number(currentUser.id) === Number(userRow.id)) {
      return Alert.alert("Users", "You cannot delete your own logged-in user account.");
    }
    const name = userRow.name || userRow.username;
    if (confirm(`Are you sure you want to permanently delete user account "${name}"?`)) {
      try {
        await api.deleteUser(userRow.id);
        setMessage({ type: "success", text: `User "${name}" deleted successfully.` });
        load();
      } catch (error) {
        setMessage({ type: "error", text: error.message });
      }
    }
  };

  const resetMfa = async (userRow) => {
    if (isTargetSuperAdmin(userRow) && !isCurrentSuperAdmin) {
      return Alert.alert("Permission Denied", "Administrators cannot reset the Super Admin's MFA.");
    }
    const name = userRow.name || userRow.username;
    if (confirm(`Are you sure you want to reset MFA for "${name}"? On their next login, they will be required to scan a new QR code.`)) {
      try {
        const res = await api.resetUserMfa(userRow.id);
        setMessage({ type: "success", text: res.message || `MFA for "${name}" has been reset.` });
        load();
      } catch (error) {
        setMessage({ type: "error", text: error.message });
      }
    }
  };

  return (
    <View style={styles.page}>
      <ScreenHeader
        title="User Accounts & Access"
        subtitle="Manage system user credentials, permissions, and status. Admins can manage all users except the permanent Super Admin."
      />

      {message ? (
        <Text style={[styles.message, message.type === "error" ? styles.errorMessage : styles.successMessage]}>
          {message.text}
        </Text>
      ) : null}

      <Card>
        <Text style={styles.h2}>
          {form.id
            ? isEditingSuperAdmin
              ? "Edit Super Admin Account (Protected)"
              : "Edit User Account"
            : "Add New User Account"}
        </Text>
        <View style={styles.row}>
          <Field label="Full Name *" value={form.name} onChangeText={(v) => setForm({ ...form, name: v })} style={{ flex: 2, minWidth: 200 }} />
          <Field label="Username *" value={form.username} onChangeText={(v) => setForm({ ...form, username: v })} style={{ flex: 2, minWidth: 180 }} />
          <Field label="Email Address (optional)" value={form.email} onChangeText={(v) => setForm({ ...form, email: v })} style={{ flex: 2, minWidth: 200 }} />
          <Field label="Phone" value={form.phone} onChangeText={(v) => setForm({ ...form, phone: v })} style={{ flex: 1, minWidth: 150 }} />
        </View>

        <View style={styles.row}>
          <Select
            label={isEditingSuperAdmin ? "Role Permission (Permanent)" : "Role Permission *"}
            value={form.role}
            onChangeText={(v) => setForm({ ...form, role: v })}
            disabled={isEditingSuperAdmin}
            style={{ flex: 1, minWidth: 180 }}
          >
            {isEditingSuperAdmin ? (
              <option value="superadmin">Super Admin (System Protected)</option>
            ) : null}
            <option value="admin">Administrator</option>
            <option value="manager">Manager</option>
            <option value="sales">Sales / Site Representative</option>
          </Select>

          <Select
            label={isEditingSuperAdmin ? "Account Status (Permanent)" : "Account Status *"}
            value={String(form.is_active)}
            onChangeText={(v) => setForm({ ...form, is_active: Number(v) })}
            disabled={isEditingSuperAdmin}
            style={{ flex: 1, minWidth: 140 }}
          >
            <option value="1">Active</option>
            {!isEditingSuperAdmin ? <option value="0">Inactive</option> : null}
          </Select>

          <PasswordInput
            label={form.id ? "New Password (optional)" : "Password *"}
            value={form.password}
            onChangeText={(v) => setForm({ ...form, password: v })}
            style={{ flex: 2, minWidth: 200 }}
          />
        </View>

        <Toolbar>
          <Button icon={Save} onPress={save}>{form.id ? "Update User Account" : "Create User Account"}</Button>
          {form.id ? <Button variant="secondary" onPress={() => setForm(empty)}>Cancel Edit</Button> : null}
        </Toolbar>
      </Card>

      <Card style={{ padding: 14 }}>
        <Toolbar>
          <Field label="Search Users" value={q} onChangeText={setQ} placeholder="Name, email, username..." style={{ flex: 2, minWidth: 220 }} />
          <Select label="Role Filter" value={role} onChangeText={setRole} style={{ minWidth: 150 }}>
            <option value="">All Roles</option>
            <option value="superadmin">Super Admin</option>
            <option value="admin">Administrator</option>
            <option value="manager">Manager</option>
            <option value="sales">Sales</option>
          </Select>
          <Select label="Status Filter" value={status} onChangeText={setStatus} style={{ minWidth: 150 }}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          <Button icon={Search} onPress={load}>Search Users</Button>
        </Toolbar>
      </Card>

      <DataTable
        actionsWidth={56}
        columns={[
          { key: "username", label: "Username", flex: 1.2, style: { fontWeight: "700" } },
          { key: "name", label: "Full Name", flex: 1.5, render: (r) => r.name || r.full_name || "-" },
          { key: "email", label: "Email Address", flex: 1.6, render: (r) => r.email || "-" },
          {
            key: "role",
            label: "Role",
            flex: 1.1,
            render: (r) => (
              <Badge variant={normalizeRole(r.role) === "superadmin" ? "danger" : normalizeRole(r.role) === "admin" ? "success" : normalizeRole(r.role) === "manager" ? "info" : "secondary"}>
                {getRoleLabel(r.role).toUpperCase()}
              </Badge>
            )
          },
          {
            key: "is_active",
            label: "Status",
            flex: 0.9,
            render: (r) => (
              <Badge variant={Number(r.is_active) ? "success" : "danger"}>
                {Number(r.is_active) ? "ACTIVE" : "INACTIVE"}
              </Badge>
            )
          },
          {
            key: "mfa_enabled",
            label: "MFA Security",
            flex: 1.1,
            render: (r) => (
              <Badge variant={Number(r.mfa_enabled) ? "success" : "warning"}>
                {Number(r.mfa_enabled) ? "MFA ACTIVE" : "SETUP PENDING"}
              </Badge>
            )
          },
          { key: "created_at", label: "Created", flex: 1 }
        ]}
        rows={rows}
        renderActions={(row) => {
          const isRowSuperAdmin = isTargetSuperAdmin(row);

          if (isRowSuperAdmin) {
            // If logged-in user is NOT Super Admin: Admin cannot manage Super Admin
            if (!isCurrentSuperAdmin) {
              return (
                <View
                  title="Super Admin Account (Protected)"
                  style={{ width: 34, height: 34, borderRadius: 6, alignItems: "center", justifyContent: "center", backgroundColor: "#f1f5f9" }}
                >
                  <ShieldCheck size={18} color="#64748b" />
                </View>
              );
            }

            // If logged-in user is Super Admin: can edit details, reset password, reset MFA, but cannot deactivate or delete
            return (
              <ActionMenu
                actions={[
                  { label: "Edit Account Details", icon: UserCheck, onPress: () => startEdit(row) },
                  { label: "Reset Password", icon: KeyRound, onPress: () => resetPassword(row.id) },
                  Number(row.mfa_enabled) === 1 && { label: "Reset MFA (Re-bind Device)", icon: ShieldAlert, variant: "warning", onPress: () => resetMfa(row) }
                ]}
              />
            );
          }

          // For all other users: Admin and Super Admin can manage completely
          return (
            <ActionMenu
              actions={[
                { label: "Edit User Account", icon: UserCheck, onPress: () => startEdit(row) },
                { label: "Reset Password", icon: KeyRound, onPress: () => resetPassword(row.id) },
                Number(row.mfa_enabled) === 1 && { label: "Reset MFA (Lost Phone)", icon: ShieldAlert, variant: "warning", onPress: () => resetMfa(row) },
                {
                  label: Number(row.is_active) ? "Deactivate Account" : "Activate Account",
                  icon: Power,
                  variant: Number(row.is_active) ? "danger" : "success",
                  onPress: () => toggle(row.id)
                },
                currentUser?.id && Number(currentUser.id) !== Number(row.id) && {
                  label: "Delete User",
                  icon: Trash2,
                  variant: "danger",
                  onPress: () => removeUser(row)
                }
              ]}
            />
          );
        }}
      />
    </View>
  );
}
